import React, {useEffect, useState} from 'react';
import SimpleBar from 'simplebar-react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {CSSTransition} from 'react-transition-group';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faBagShopping, faCalendar, faChartSimple, faCheck, faChevronDown, faCode, faCog,
  faCar, faCube, faGlobe, faMessage, faPercent, faSignOutAlt,
  faSlidersH, faTable, faTag, faTimes, faUser, faUsers, faWallet
} from '@fortawesome/free-solid-svg-icons';
import {Badge, Button, Dropdown, Form, Image, Modal, Nav, Navbar} from '@themesberg/react-bootstrap';

import {Routes} from '../routes';
// Variante détourée (sans le disque bleu) : la sidebar et la barre mobile sont sur fond navy.
import Logo from '../assets/img/logo-white.svg';
import {EMAIL, LANGUAGE, NAME, WEBSITE} from '../services/constants';
import {FormattedMessage} from 'react-intl';
import Utils from '../services/Utils';
import API from '../services/API';
import {useNotifications} from '../services/NotificationsContext';
import {usePlan} from '../services/PlanContext';
import AccountModal from './AccountModal';
import WelcomeModal from './WelcomeModal';
import LANGUAGES from '../services/languages';

export default (props = {}) => {
  const {intl} = props;
  const history = useHistory();
  const location = useLocation();
  const {pathname} = location;
  // Menu replié par défaut : sur desktop la classe `d-md-flex` force l'affichage
  // quoi qu'il arrive, donc ce `false` ne concerne que le mobile (sinon le menu
  // s'ouvrait par-dessus la page au chargement/rafraîchissement).
  const [show, setShow] = useState(false);
  const [showLangPanel, setShowLangPanel] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  // Relance « n° de TVA manquant » : ouvre « Mon compte » avec les deux champs de facturation soulignés.
  const [highlightBilling, setHighlightBilling] = useState(false);
  const [vatReminderDone, setVatReminderDone] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [language, setLanguage] = useState(Utils.getLanguage());
  const {newBookingsCount} = useNotifications();
  const {openSubscription, vatReminder, paymentFailed, expired} = usePlan();
  const [paymentFailedNotified, setPaymentFailedNotified] = useState(false);
  const showClass = show ? 'show' : '';
  const name = localStorage.getItem(NAME);
  const email = localStorage.getItem(EMAIL);

  // Avatar : initiales plutôt qu'une image générique. Deux lettres max tirées du nom, repli sur
  // la première lettre de l'email ; si les deux sont vides, le rendu bascule sur une icône.
  const initials = (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase() || (email || '').charAt(0).toUpperCase();

  // First-login detection: ask the backend whether this user has already
  // completed their profile (dedicated flag on users.profile_completed_at).
  useEffect(() => {
    if (!Utils.isAccessToken(history, true)) return;
    API.getProfileStatus(history).then(response => {
      if (response && response.data && response.data.success === true) {
        if (response.data.data && response.data.data.profileCompleted === false) {
          setShowWelcomeModal(true);
        }
      }
    });
  }, []);

  // Abonné dont le client Stripe ne porte aucun n° de TVA : ses factures partent sans numéro
  // intracom. Le backend ne lève le drapeau que s'il n'a pas pu le retrouver tout seul, et une seule
  // fois par compte — beaucoup de chauffeurs (franchise en base) n'en ont légitimement pas.
  // Le Sidebar est monté sur toutes les pages admin : la relance suit le chauffeur où qu'il soit.
  useEffect(() => {
    if (!vatReminder || vatReminderDone) return;
    setVatReminderDone(true);
    setHighlightBilling(true);
    setShowAccountModal(true);
    Utils.notifyWarning(intl.formatMessage({id: 'VAT_REMINDER_TITLE'}),
                        intl.formatMessage({id: 'VAT_REMINDER_MESSAGE'}));
  }, [vatReminder, vatReminderDone, intl]);

  // Impayé sur l'abonnement : on prévient une fois par session (le détail et le bouton vers le portail
  // Stripe sont dans « Mon abonnement »). Le backend lève le drapeau depuis Stripe, pas depuis le SPA.
  useEffect(() => {
    if (!paymentFailed || paymentFailedNotified) return;
    setPaymentFailedNotified(true);
    Utils.notifyWarning(intl.formatMessage({id: 'PAYMENT_FAILED_TITLE'}),
                        intl.formatMessage({id: expired ? 'PAYMENT_FAILED_EXPIRED_TOAST' : 'PAYMENT_FAILED_TOAST'}));
  }, [paymentFailed, paymentFailedNotified, expired, intl]);

  // Fermeture de « Mon compte » : si elle avait été ouverte par la relance, on la marque comme vue —
  // que le chauffeur ait complété ou non, puisqu'une absence de numéro est une réponse valable.
  const closeAccountModal = () => {
    setShowAccountModal(false);
    if (highlightBilling) {
      setHighlightBilling(false);
      API.postVatReminderSeen(history);
    }
  };

  const handleLogout = () => Utils.logout(history);

  const handleChangeLanguage = (code) => {
    setLanguage(code);
    localStorage.setItem(LANGUAGE, code);
    API.getLanguage(history, code).then(() => window.location.reload());
  };

  const handleContactUs = (e) => {
    e.preventDefault();
    API.postContactUs(history, contactMessage).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'MESSAGE_SENT'}), intl.formatMessage({id: 'WE_WILL_PROCESS_REQUEST'}));
        setShowContactModal(false);
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  };

  const onCollapse = () => setShow(!show);

  const NavItem = ({title, link, icon, badgeText, indent, onClick}) => {
    const active = link === pathname ? 'active' : '';
    const linkProps = link ? {as: Link, to: link} : {};
    return (
      <Nav.Item className={active} onClick={() => { setShow(false); if (onClick) onClick(); }}>
        <Nav.Link {...linkProps} className={`ccv2-nav-link ${indent ? 'ccv2-nav-child' : ''}`}>
          <span className="ccv2-nav-label">
            {icon && <span className="sidebar-icon"><FontAwesomeIcon icon={icon}/></span>}
            <span className="sidebar-text">{title}</span>
          </span>
          {badgeText ? (
            <Badge pill bg="tertiary" text="primary" className="ccv2-nav-badge">{badgeText}</Badge>
          ) : null}
        </Nav.Link>
      </Nav.Item>
    );
  };

  return (
    <>
      {/* Mobile top bar (toggle sidebar) — conservé à l'identique */}
      <Navbar expand={false} collapseOnSelect variant="dark" className="navbar-theme-primary px-4 d-md-none ccv2-mobile-topbar">
        <Navbar.Brand className="me-lg-5 d-flex align-items-center" as={Link} to={Routes.DashboardOverview.path}>
          <Image src={Logo} className="navbar-brand-light"/>
          <span className="fw-bold text-white ms-2">Click<span className="cc-brand-accent">Chauffeur</span></span>
        </Navbar.Brand>
        <Navbar.Toggle as={Button} aria-controls="main-navbar" onClick={onCollapse}>
          <span className="navbar-toggler-icon"/>
        </Navbar.Toggle>
      </Navbar>

      <CSSTransition timeout={300} in={show} classNames="sidebar-transition">
        <div className={`collapse ${showClass} sidebar d-md-flex bg-primary text-white ccv2-sidebar`}>
          <SimpleBar className="ccv2-sidebar-scroll">
            <div className="ccv2-sidebar-inner">

              {/* Brand */}
              {/* Le conteneur porte l'espacement, l'ancre s'ajuste au contenu : la zone
                  cliquable se limite ainsi au pictogramme et au texte. */}
              <div className="ccv2-sidebar-brand d-none d-md-block">
                <a href={WEBSITE} target="_blank" rel="noopener noreferrer"
                   className="ccv2-sidebar-brand-link">
                  <Image src={Logo} className="ccv2-sidebar-logo"/>
                  <span className="ccv2-sidebar-brand-name">Click<span className="cc-brand-accent">Chauffeur</span></span>
                </a>
              </div>

              {/* Mobile menu header: brand à gauche + bouton de fermeture clair à droite */}
              <div className="d-flex d-md-none align-items-center justify-content-between ccv2-mobile-menu-head">
                <span className="ccv2-mobile-menu-brand">
                  <Image src={Logo}/>
                  <span>Click<span className="cc-brand-accent">Chauffeur</span></span>
                </span>
                <button type="button" className="ccv2-mobile-close" aria-label="Fermer le menu" onClick={onCollapse}>
                  <FontAwesomeIcon icon={faTimes}/>
                </button>
              </div>

              {/* Top-level nav */}
              <Nav className="flex-column ccv2-nav">
                <NavItem title={intl.formatMessage({id: 'DASHBOARD'})} link={Routes.DashboardOverview.path} icon={faChartSimple}/>
                <NavItem title={intl.formatMessage({id: 'BOOKINGS'})}  link={Routes.Orders.path}             icon={faBagShopping} badgeText={newBookingsCount > 0 ? newBookingsCount : undefined}/>
                <NavItem title={intl.formatMessage({id: 'CUSTOMERS'})} link={Routes.Customers.path}          icon={faUsers}/>
                <NavItem title={intl.formatMessage({id: 'PLANNING'})}  link={Routes.Planning.path}           icon={faCalendar}/>
              </Nav>

              {/* MODULE section header */}
              <div className="ccv2-section-header">
                <span className="ccv2-section-divider"/>
                <span className="ccv2-section-label">
                  <FontAwesomeIcon icon={faTable} className="me-2"/>
                  <FormattedMessage id="MODULE"/>
                </span>
                <span className="ccv2-section-divider"/>
              </div>

              <Nav className="flex-column ccv2-nav">
                <NavItem title={intl.formatMessage({id: 'SETTINGS'})}         link={Routes.Settings.path}        icon={faCog}      indent/>
                <NavItem title={intl.formatMessage({id: 'VEHICLES'})}         link={Routes.Vehicles.path}        icon={faCar}      indent/>
                <NavItem title={intl.formatMessage({id: 'PACKAGES'})}         link={Routes.Packages.path}        icon={faCube}     indent/>
                <NavItem title={intl.formatMessage({id: 'SURCHARGES'})}       link={Routes.Surcharges.path}      icon={faPercent}  indent/>
                <NavItem title={intl.formatMessage({id: 'OPTIONS'})}          link={Routes.Options.path}         icon={faSlidersH} indent/>
                <NavItem title={intl.formatMessage({id: 'DISCOUNT_CODES'})}   link={Routes.DiscountCodes.path}   icon={faTag}      indent/>
                <NavItem title={intl.formatMessage({id: 'BOOKING_PAGE'})}     link={Routes.BookingFields.path}   icon={faGlobe}    indent/>
                <NavItem title={intl.formatMessage({id: 'HTML_INTEGRATION'})} link={Routes.HtmlIntegration.path} icon={faCode}     indent/>
              </Nav>

            </div>
          </SimpleBar>

          {/* Footer: support + profile — hors SimpleBar, reste collé en bas */}
          <div className="ccv2-sidebar-footer">
            <Nav className="flex-column ccv2-nav">
              <NavItem
                title={intl.formatMessage({id: 'CONTACT_US'})}
                icon={faMessage}
                onClick={() => setShowContactModal(true)}
              />
            </Nav>

            <Dropdown className="ccv2-profile" drop="up">
              <Dropdown.Toggle as="div" className="ccv2-profile-toggle">
                <span className="ccv2-profile-avatar" aria-hidden="true">
                  {initials || <FontAwesomeIcon icon={faUser}/>}
                </span>
                <div className="ccv2-profile-info">
                  <div className="ccv2-profile-name">{name}</div>
                  <div className="ccv2-profile-email">{email}</div>
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="ccv2-profile-chevron"/>
              </Dropdown.Toggle>
              <Dropdown.Menu className="ccv2-profile-menu">
                <Dropdown.Item className="fw-bold" onClick={() => setShowAccountModal(true)}>
                  <FontAwesomeIcon icon={faUser} className="me-2"/>
                  <FormattedMessage id="MY_ACCOUNT"/>
                </Dropdown.Item>
                <Dropdown.Item className="fw-bold" onClick={openSubscription}>
                  <FontAwesomeIcon icon={faWallet} className="me-2"/>
                  <FormattedMessage id="MY_SUBSCRIPTION"/>
                </Dropdown.Item>

                {/* Language submenu — s'ouvre en place dans le panel.
                    Plain <button> au lieu de Dropdown.Item pour éviter l'auto-fermeture
                    du menu au clic (le handler interne de Dropdown.Item ferme le parent). */}
                <Dropdown.Divider/>
                <button
                  type="button"
                  className="dropdown-item fw-bold ccv2-lang-toggle"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowLangPanel(v => !v); }}
                >
                  <FontAwesomeIcon icon={faGlobe} className="me-2"/>
                  <FormattedMessage id="LANGUAGE" defaultMessage="Langue"/>
                  <span className="ccv2-lang-current ms-auto">
                    {(LANGUAGES.find(l => l.code === language) || LANGUAGES[0]).flag}
                    {' '}
                    {(LANGUAGES.find(l => l.code === language) || LANGUAGES[0]).label}
                  </span>
                </button>
                {showLangPanel && (
                  <div className="ccv2-lang-panel">
                    {LANGUAGES.map(l => (
                      <button
                        key={l.code}
                        type="button"
                        className={`ccv2-lang-option ${l.code === language ? 'active' : ''}`}
                        onClick={() => handleChangeLanguage(l.code)}
                      >
                        <span className="ccv2-lang-flag">{l.flag}</span>
                        <span className="ccv2-lang-label">{l.label}</span>
                        {l.code === language && (
                          <FontAwesomeIcon icon={faCheck} className="ccv2-lang-check"/>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <Dropdown.Divider/>
                <Dropdown.Item className="fw-bold" onClick={handleLogout}>
                  <FontAwesomeIcon icon={faSignOutAlt} className="text-danger me-2"/>
                  <FormattedMessage id="LOGOUT"/>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </CSSTransition>

      <AccountModal intl={intl} show={showAccountModal} highlightBilling={highlightBilling}
                    onClose={closeAccountModal}/>
      <WelcomeModal intl={intl} show={showWelcomeModal} onClose={() => setShowWelcomeModal(false)}/>

      <Modal as={Modal.Dialog} centered show={showContactModal} onHide={() => setShowContactModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="CONTACT_US"/></Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowContactModal(false)}/>
        </Modal.Header>
        <Modal.Body>
          <Form id="contact-form" className="text-center" onSubmit={handleContactUs}>
            <FormattedMessage id="FOR_ALL_ENQUIRIES"/>
            <br/><br/>
            <Form.Control required as="textarea" rows="6" id="message"
                          onChange={(e) => setContactMessage(e.target.value)} value={contactMessage}/>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" type="submit" form="contact-form">
            <FormattedMessage id="SEND"/>
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowContactModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
